import { useState, type ReactNode } from 'react'
import Box from '@mui/material/Box'

import AppMenu from '../../../components/AppMenu'
import { APP_HEADER_HEIGHT } from '../../helpers/utils'

export type AppShellProps = {
	children: ReactNode,
}

/**
 * MUI app frame: collapsible sidebar rail + main content region, offset below
 * the fixed universal header. Owns the sidebar expanded/collapsed state (the
 * header exposes no slot for the toggle, so it lives here in our frame).
 */
export default function AppShell( { children }: AppShellProps ) {
	const [ sidebarOpen, setSidebarOpen ] = useState( true )
	const toggleSidebar = () => setSidebarOpen( ( prev ) => !prev )

	return (
		// Padding, not margin, clears the fixed header. A top margin here collapses
		// up through #root into <body>, so <body> itself starts 56px down. That is
		// invisible until an EUI tooltip mounts: it sets `body { position: relative }`
		// (euiBody-hasPortalContent), and every absolutely-positioned portal — EUI
		// popovers included — then drops by 56px while the tooltip is open.
		<Box sx={{ display: 'flex', height: '100vh', boxSizing: 'border-box', pt: `${APP_HEADER_HEIGHT}px` }}>
			<AppMenu open={sidebarOpen} onToggle={toggleSidebar} />
			<Box component="main" sx={{ flexGrow: 1, minWidth: 0, height: '100%', overflow: 'auto' }}>
				{children}
			</Box>
		</Box>
	)
}
